import React, { useState } from "react";
import { useLoading, useApiError, useGoogleAuth } from "../../hooks";
import axios from "axios";

interface LoginFormProps {
  onSubmit: (data: { email: string }) => Promise<void>;
  onSocialLoginSuccess?: (result: any) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  onSocialLoginSuccess,
}) => {
  const [formData, setFormData] = useState({
    email: "",
  });

  const { isLoading, withLoading } = useLoading();
  const { handleApiError } = useApiError();
  const {
    signInWithGoogle,
    isLoading: isGoogleLoading,
    isConfigured,
  } = useGoogleAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      return;
    }

    try {
      await withLoading(async () => {
        await onSubmit(formData);
      });
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const googleResult = await signInWithGoogle();
      if (!googleResult) return; // User cancelled

      // Call our backend with the Firebase ID token
      const response = await axios.post("/api/auth/social-login", {
        idToken: googleResult.idToken,
      });

      if (response.data.success && onSocialLoginSuccess) {
        onSocialLoginSuccess(response.data);
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  return (
    <div className="login-container">
      <div className="login-header">
        <h1 className="login-title">Benvenuto in CosaVorrei</h1>
        <p className="login-subtitle">
          Inserisci la tua email per ricevere il link di accesso.
        </p>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            La tua email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="la-tua-email@esempio.com"
            required
            disabled={isLoading}
            className="form-input"
          />
        </div>

        <button
          type="submit"
          className="login-button"
          disabled={isLoading || !formData.email.trim()}
        >
          {isLoading ? (
            <>
              <div className="loading-spinner"></div>
              <span>Invio in corso...</span>
            </>
          ) : (
            "Invia link di accesso"
          )}
        </button>
      </form>

      {isConfigured && (
        <>
          <div className="login-divider">
            <span>o</span>
          </div>

          <button
            type="button"
            className="google-login-button"
            onClick={handleGoogleLogin}
            disabled={isLoading || isGoogleLoading}
          >
            {isGoogleLoading ? (
              <span>Accesso in corso...</span>
            ) : (
              <>
                <svg
                  className="google-icon"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continua con Google</span>
              </>
            )}
          </button>
        </>
      )}

      <div className="login-help">
        <p>
          Non hai ancora un gruppo?{" "}
          <a href="/create-group" className="create-group-link">
            Crea un nuovo gruppo
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
